import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAdvertisers,
  createAdvertiser as apiCreateAdvertiser,
  updateAdvertiserStatus as apiUpdateAdvertiserStatus,
} from "../../../api/ads";
import type { Advertiser, AdvertiserListParams, CreateAdvertiserPayload } from "../../../api/ads";
import { AdsAdvertiserStatus } from "../../../api/ads";

export const useAdvertisers = (initialParams: AdvertiserListParams = {}) => {
  const defaultParams: AdvertiserListParams = useMemo(
    () => ({
      pageNum: 1,
      pageSize: 10,
      status: "ALL",
      search: "",
      ...initialParams,
    }),
    [initialParams]
  );

  const [params, setParamsState] = useState<AdvertiserListParams>(defaultParams);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const fetchAdvertisers = useCallback(
    async (override?: Partial<AdvertiserListParams>) => {
      try {
        setListLoading(true);
        setError(null);

        const merged: AdvertiserListParams = { ...params, ...override };
        const response = await getAdvertisers(merged);

        setAdvertisers(response.results);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      } catch (err: any) {
        setError(err?.message || "Failed to load advertisers");
      } finally {
        setListLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    fetchAdvertisers();
  }, [fetchAdvertisers]);

  const setParams = useCallback((next: Partial<AdvertiserListParams>) => {
    setParamsState((prev) => ({ ...prev, ...next, pageNum: 1 }));
  }, []);

  const setPageNum = useCallback((pageNum: number) => {
    setParamsState((prev) => ({ ...prev, pageNum }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setParamsState((prev) => ({ ...prev, pageSize, pageNum: 1 }));
  }, []);

  const updateStatus = useCallback(
    async (id: string, status: AdsAdvertiserStatus): Promise<string> => {
      setActionLoading(true);
      setError(null);
      try {
        const result = await apiUpdateAdvertiserStatus(id, status);
        setAdvertisers((prev) => prev.map((a) => (a.id === id ? result.advertiser : a)));
        return result.message;
      } catch (err: any) {
        setError(err?.message || "Failed to update advertiser status");
        throw err;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const approveAdvertiser = useCallback((id: string) => updateStatus(id, AdsAdvertiserStatus.APPROVED), [updateStatus]);
  const rejectAdvertiser = useCallback((id: string) => updateStatus(id, AdsAdvertiserStatus.REJECTED), [updateStatus]);
  const blockAdvertiser = useCallback((id: string) => updateStatus(id, AdsAdvertiserStatus.DISABLED), [updateStatus]);

  const createAdvertiser = useCallback(
    async (payload: CreateAdvertiserPayload): Promise<string> => {
      setCreateLoading(true);
      try {
        const result = await apiCreateAdvertiser(payload);
        await fetchAdvertisers();
        return result.message;
      } finally {
        setCreateLoading(false);
      }
    },
    [fetchAdvertisers]
  );

  return {
    functions: {
      fetchAdvertisers,
      setParams,
      setPageNum,
      setPageSize,
      approveAdvertiser,
      rejectAdvertiser,
      blockAdvertiser,
      createAdvertiser,
    },
    values: {
      advertisers,
      params,
      totalCount,
      totalPages,
      error,
    },
    loading: {
      listLoading,
      actionLoading,
      createLoading,
    },
  };
};

